import { i18n } from '@pypestream/translations';

import { buildAgentOverride, PRO_STUDIO_NAMESPACES } from './agent-vocabulary';

/**
 * Installs / removes the Agent vocabulary overlay on the live i18next instance.
 *
 * Kept apart from `agent-vocabulary.ts` so the transform stays pure and
 * unit-testable; everything that touches i18next lives here.
 *
 * ## Why a snapshot
 *
 * `addResourceBundle(..., deep, overwrite)` writes over the store in place, so
 * once the Agent wording is in, the original strings are gone from memory.
 * Turning the flag back off has to restore something — hence the snapshot taken
 * before the first override. Without it, switching the flag off mid-session
 * would leave the Agent wording stuck until a reload.
 */

type Bundle = Record<string, unknown>;

/** Original bundles, captured once, keyed `lng::ns`. */
const originals = new Map<string, Bundle>();
let installed = false;
/** True while `notifyReRender` is emitting its own `languageChanged`. */
let notifying = false;

const languagesToPatch = (): string[] => {
  // `languages` is the resolved chain (e.g. ['en-GB','en']); `language` alone
  // misses the fallback that most namespaces actually resolve against.
  const chain = (i18n.languages ?? []).filter(Boolean);
  const current = i18n.language ? [i18n.language] : [];
  return Array.from(new Set([...current, ...chain, 'en']));
};

const snapshotOnce = (lng: string, ns: string): void => {
  const key = `${lng}::${ns}`;
  if (originals.has(key)) return;

  const current = i18n.getResourceBundle(lng, ns) as Bundle | undefined;
  if (current) originals.set(key, JSON.parse(JSON.stringify(current)));
};

const applyOverrides = (): void => {
  languagesToPatch().forEach((lng) => {
    PRO_STUDIO_NAMESPACES.forEach((ns) => {
      snapshotOnce(lng, ns);
      const base = i18n.getResourceBundle(lng, ns) as Bundle | undefined;
      if (!base) return;
      i18n.addResourceBundle(lng, ns, buildAgentOverride(base, ns), true, true);
    });
  });
};

const restoreOriginals = (): void => {
  // Driven off the SNAPSHOT map, not `languagesToPatch()`: the overlay is
  // re-applied on every language change, so by the time it is switched off the
  // snapshot can hold languages that are no longer in the current resolution
  // chain. Walking the chain instead would leave those languages stuck on the
  // Agent wording until a reload.
  originals.forEach((original, key) => {
    const separator = key.indexOf('::');
    i18n.addResourceBundle(
      key.slice(0, separator),
      key.slice(separator + 2),
      original,
      true,
      true
    );
  });
};

/**
 * Make every mounted component re-read the store.
 *
 * `react-i18next` binds `languageChanged` by default (`bindI18n` in its
 * defaults), so the event is what re-renders `useTranslation` /
 * `TranslationComponent`. Without it components keep the strings they already
 * rendered.
 *
 * The CURRENT language must be passed: `initI18n`'s own `languageChanged`
 * handler does `documentElement.setAttribute('lang', lng)`, so a bare emit
 * writes `<html lang="undefined">` — which breaks `:lang()` styling and tells
 * assistive tech the page has no language. react-i18next's handler ignores the
 * payload, so passing it costs nothing there.
 */
const notifyReRender = (): void => {
  // The cast is the i18next TYPES being narrower than the emitter: `emit` is
  // declared `(eventName: string) => void`, while the implementation is
  // `emit(event, ...args)` and forwards every extra argument to the listeners.
  // The payload is required (see above), so the call is widened rather than
  // dropped — and it is the OBJECT that is cast, not the method: pulling
  // `i18n.emit` out would detach the receiver, and `emit` reads
  // `this.observers`.
  //
  // `notifying` is what stops `handleLanguageChanged` (below) treating this
  // synthetic emit as a real language change and re-installing on top of the
  // install that is emitting it.
  notifying = true;
  try {
    (
      i18n as unknown as { emit: (event: string, ...args: unknown[]) => void }
    ).emit('languageChanged', i18n.language);
  } finally {
    notifying = false;
  }
};

/**
 * Re-apply the overlay after a REAL language change.
 *
 * `applyOverrides` walks the resolution chain as it stands when it runs, and
 * i18next loads a language's bundles fresh when it is switched to — so a
 * language selected after the flag resolved never gets overridden. Without
 * this, switching from English to any other locale with the flag on silently
 * reverts every renamed string ("Agents" back to "Solutions", the nav back to
 * "Workflows") for the rest of the session.
 *
 * Bound only while the overlay is installed, and ignores the synthetic emit
 * `notifyReRender` uses to force a re-render.
 */
const handleLanguageChanged = (): void => {
  if (notifying || !installed) return;
  loadThenApply();
};

/**
 * Wait for the locale chunks, then override the bundles that are certainly
 * present.
 *
 * Namespaces load LAZILY. `initializeI18n` registers a backend whose `read` is
 * a dynamic `import()` of the locale JSON, so a namespace is absent from the
 * store until its chunk lands — and overriding a bundle that is not there yet
 * does nothing.
 *
 * This used to override whatever happened to have loaded by the time the flag
 * resolved and then set `installed = true` regardless, so every namespace that
 * lost the race was skipped for the WHOLE SESSION with no retry. The symptom
 * was the worst kind: the flag half-worked, differently on each load, and the
 * nav kept saying "Workflows" while other screens renamed correctly.
 */
const loadThenApply = (): void => {
  i18n.loadNamespaces([...PRO_STUDIO_NAMESPACES], () => {
    // The flag can flip off while the chunks are in flight; the `installed`
    // check keeps the last call the winner.
    if (!installed) return;
    applyOverrides();
    notifyReRender();
  });
};

/**
 * Turn the Agent vocabulary on or off. Safe to call repeatedly with the same
 * value — it no-ops.
 *
 * Turning it ON is ASYNCHRONOUS: it waits for the locale chunks. Turning it off
 * is synchronous, because by then the snapshots are already in memory.
 */
export const setAgentVocabulary = (enabled: boolean): void => {
  if (enabled === installed) return;
  installed = enabled;

  if (!enabled) {
    i18n.off('languageChanged', handleLanguageChanged);
    restoreOriginals();
    notifyReRender();
    return;
  }

  // Re-apply on every subsequent language change — see `handleLanguageChanged`.
  i18n.off('languageChanged', handleLanguageChanged);
  i18n.on('languageChanged', handleLanguageChanged);
  loadThenApply();
};

/** Test seam — forget the snapshot and the installed flag. */
export const resetAgentVocabularyForTests = (): void => {
  originals.clear();
  installed = false;
  notifying = false;
  i18n.off?.('languageChanged', handleLanguageChanged);
};
